import { useState } from 'react';
import { CheckCircle2, XCircle, ChevronRight, ChevronDown, AlertTriangle, Info } from 'lucide-react';
import { getScoreColor } from '@/lib/utils';
import type { SubPanelId, MetricInsight } from './types';
import { MetricExplainer } from './InsightComponents';

export function ScoreBlock({ label, score }: { label: string; score: number }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-light ${getScoreColor(score)}`}>{score}</p>
    </div>
  );
}

export function StatCard({ label, value, isWarning }: { label: string; value: number; isWarning?: boolean }) {
  return (
    <div className="bg-secondary/30 rounded-lg p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`text-2xl font-light ${isWarning && value > 0 ? 'text-orange-400' : ''}`}>{value}</p>
    </div>
  );
}

export function SubPanelNav({ panels, active, onChange }: { panels: { id: SubPanelId; label: string }[]; active: SubPanelId; onChange: (id: SubPanelId) => void }) {
  return (
    <div className="flex gap-2 flex-wrap">
      {panels.map((panel) => (
        <button
          key={panel.id}
          onClick={() => onChange(panel.id)}
          className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${
            active === panel.id
              ? 'bg-foreground text-background'
              : 'bg-secondary text-muted-foreground hover:text-foreground'
          }`}
        >
          {panel.label}
        </button>
      ))}
    </div>
  );
}

export function FilterButton({
  active,
  onClick,
  children,
  variant = 'default',
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  variant?: 'default' | 'critical' | 'high' | 'medium';
}) {
  const variants = {
    default: active ? 'bg-foreground text-background' : 'bg-secondary text-muted-foreground hover:text-foreground',
    critical: active ? 'bg-red-500/20 text-red-400 border border-red-500/30' : 'bg-secondary text-muted-foreground hover:text-foreground border border-transparent',
    high: active ? 'bg-orange-500/20 text-orange-400 border border-orange-500/30' : 'bg-secondary text-muted-foreground hover:text-foreground border border-transparent',
    medium: active ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' : 'bg-secondary text-muted-foreground hover:text-foreground border border-transparent',
  };

  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${variants[variant]}`}
    >
      {children}
    </button>
  );
}

export function MetricCardWithAI({
  label,
  fullName,
  value,
  rating,
  insight,
  metricKey,
}: {
  label: string;
  fullName: string;
  value: string;
  rating: 'good' | 'needs-improvement' | 'poor';
  insight: MetricInsight | null;
  metricKey?: 'lcp' | 'cls' | 'fcp' | 'ttfb';
}) {
  const colors = {
    good: 'text-green-400 border-green-500/20',
    'needs-improvement': 'text-orange-400 border-orange-500/20',
    poor: 'text-red-400 border-red-500/20',
  };

  return (
    <div className={`bg-secondary/30 rounded-lg p-4 border ${colors[rating]}`}>
      <div className="flex justify-between items-start">
        <div>
          <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
          <p className="text-xs text-muted-foreground">{fullName}</p>
        </div>
        <span className={`px-2 py-1 rounded text-xs ${
          rating === 'good' ? 'bg-green-500/20 text-green-400' :
          rating === 'needs-improvement' ? 'bg-orange-500/20 text-orange-400' :
          'bg-red-500/20 text-red-400'
        }`}>
          {rating === 'good' ? 'Good' : rating === 'needs-improvement' ? 'Needs Work' : 'Poor'}
        </span>
      </div>
      <p className={`text-3xl font-light mt-2 ${colors[rating].split(' ')[0]}`}>{value}</p>
      <MetricExplainer insight={insight} metricKey={metricKey} />
    </div>
  );
}

export function MetricRow({ label, sublabel, value, rating, target }: { label: string; sublabel: string; value: string; rating: string; target: string }) {
  const colors: Record<string, string> = {
    good: 'text-green-400',
    'needs-improvement': 'text-orange-400',
    poor: 'text-red-400',
  };

  return (
    <tr className="border-b border-border/50">
      <td className="p-4">
        <p className="font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{sublabel}</p>
      </td>
      <td className={`p-4 font-mono ${colors[rating] || ''}`}>{value}</td>
      <td className="p-4">
        <span className={`px-2 py-1 rounded text-xs ${
          rating === 'good' ? 'bg-green-500/20 text-green-400' :
          rating === 'needs-improvement' ? 'bg-orange-500/20 text-orange-400' :
          'bg-red-500/20 text-red-400'
        }`}>
          {rating === 'good' ? 'Good' : rating === 'needs-improvement' ? 'Needs Work' : 'Poor'}
        </span>
      </td>
      <td className="p-4 text-muted-foreground text-sm">{target}</td>
    </tr>
  );
}

export function CheckRow({ label, value }: { label: string; value: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm">{label}</span>
      {value ? <CheckCircle2 className="h-4 w-4 text-green-500/70" /> : <XCircle className="h-4 w-4 text-red-500" />}
    </div>
  );
}

export function SchemaField({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-muted-foreground">{label}:</span>
      {value ? (
        <span className="text-foreground text-right">{value}</span>
      ) : (
        <span className="text-red-400/50 text-right">Missing</span>
      )}
    </div>
  );
}

export function SitemapTree({ node, level = 0 }: { node: any; level?: number }) {
  const [expanded, setExpanded] = useState(level < 2);

  return (
    <div className={level > 0 ? 'ml-4 border-l border-border/30 pl-4' : ''}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 py-1 hover:text-foreground text-muted-foreground transition-colors w-full text-left"
      >
        {node.children?.length > 0 && (expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />)}
        <span className="text-sm">{node.name}</span>
        <span className="text-xs text-muted-foreground font-mono">{node.path}</span>
      </button>
      {expanded && node.children?.map((child: any, i: number) => <SitemapTree key={i} node={child} level={level + 1} />)}
    </div>
  );
}

export function FlagSection({ title, items, contentKey }: { title: string; items: any[]; contentKey: string }) {
  return (
    <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4">
      <h4 className="font-medium text-orange-400 mb-3">{title} ({items.length})</h4>
      <div className="space-y-3">
        {items.slice(0, 5).map((item, i) => (
          <div key={i} className="text-sm">
            <p className="text-muted-foreground">"{item[contentKey] || item.content || item.tag || item.title || item.description}"</p>
            <p className="text-xs text-muted-foreground mt-1">Found on: {item.pages.join(', ')}</p>
          </div>
        ))}
        {items.length > 5 && <p className="text-xs text-muted-foreground">...and {items.length - 5} more</p>}
      </div>
    </div>
  );
}

export function QualityScoreCard({
  label,
  score,
  details,
}: {
  label: string;
  score: number;
  details: { label: string; value: boolean; count?: number; total?: number }[];
}) {
  return (
    <div className="bg-secondary/30 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-medium">{label}</h4>
        <span className={`text-xl font-light ${getScoreColor(score)}`}>{score}/100</span>
      </div>
      <div className="space-y-2">
        {details.map((detail, i) => {
          const hasRatio = detail.count !== undefined && detail.total !== undefined;
          const pct = hasRatio ? (detail.total! > 0 ? detail.count! / detail.total! : 0) : 0;
          const ratioColor = pct >= 0.8 ? 'text-green-500' : pct >= 0.5 ? 'text-orange-400' : 'text-red-400';

          return (
            <div key={i} className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">{detail.label}</span>
              {hasRatio ? (
                <span className={`font-medium ${ratioColor}`}>{detail.count}/{detail.total}</span>
              ) : detail.value ? (
                <CheckCircle2 className="h-3 w-3 text-green-500" />
              ) : (
                <XCircle className="h-3 w-3 text-red-500" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function TrackingCoverageCard({
  label,
  tracked,
  total,
  colorClass,
}: {
  label: string;
  tracked: number;
  total: number;
  colorClass: string;
}) {
  const percentage = total > 0 ? Math.round((tracked / total) * 100) : 0;
  return (
    <div className="bg-secondary/30 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <span className={`text-sm font-medium ${colorClass}`}>{label}</span>
        <span className="text-lg font-light">{tracked}/{total}</span>
      </div>
      <div className="w-full bg-secondary rounded-full h-2">
        <div
          className={`h-2 rounded-full ${
            percentage === 100 ? 'bg-green-500' :
            percentage >= 50 ? 'bg-yellow-500' :
            'bg-red-500'
          }`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <p className="text-xs text-muted-foreground mt-1">{percentage}% coverage</p>
    </div>
  );
}

export function TrackingCard({ title, present, details, critical }: { title: string; present: boolean; details: { label: string; value: string | null }[]; critical?: boolean }) {
  return (
    <div className={`bg-secondary/30 rounded-lg p-4 ${!present && critical ? 'ring-1 ring-red-500/30' : ''}`}>
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium text-sm">{title}</h4>
        {present ? <CheckCircle2 className="h-4 w-4 text-green-500" /> : <XCircle className={`h-4 w-4 ${critical ? 'text-red-500' : 'text-orange-500'}`} />}
      </div>
      {present ? (
        <div className="space-y-1">
          {details.map((d) => (
            <div key={d.label} className="flex justify-between text-xs">
              <span className="text-muted-foreground">{d.label}</span>
              <span className="font-mono">{d.value || 'N/A'}</span>
            </div>
          ))}
        </div>
      ) : (
        <p className={`text-xs ${critical ? 'text-red-400' : 'text-orange-400'}`}>Not installed</p>
      )}
    </div>
  );
}

export function TrackingGapCard({ gap }: { gap: any }) {
  const priorityColors: Record<string, string> = {
    critical: 'bg-red-500/10 border-red-500/30',
    high: 'bg-orange-500/10 border-orange-500/30',
    medium: 'bg-yellow-500/10 border-yellow-500/30',
    low: 'bg-blue-500/10 border-blue-500/30',
  };

  const priorityIcons: Record<string, React.ReactNode> = {
    critical: <XCircle className="h-4 w-4 text-red-500" />,
    high: <AlertTriangle className="h-4 w-4 text-orange-500" />,
    medium: <AlertTriangle className="h-4 w-4 text-yellow-500" />,
    low: <Info className="h-4 w-4 text-blue-400" />,
  };

  return (
    <div className={`rounded-lg border p-3 ${priorityColors[gap.priority] || priorityColors.medium}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">{priorityIcons[gap.priority]}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm">{gap.event}</span>
            <span className="text-xs text-muted-foreground px-2 py-0.5 bg-secondary rounded">{gap.category}</span>
          </div>
          <p className="text-xs text-muted-foreground">{gap.description}</p>
          <p className="text-xs text-orange-400 mt-2">
            <span className="font-medium">Why it matters:</span> {gap.whyItMatters}
          </p>
        </div>
      </div>
    </div>
  );
}
